import {
  type ChangeEvent,
  type KeyboardEvent,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useRouter } from "next/compat/router";
import useSWR from "swr";
import {
  Box,
  Text,
  Heading,
  IconFa,
  Loader,
  ErrorText,
  Pagination,
  TextField,
  Button,
} from "@cruk/cruk-react-components";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import {
  searchUrlHomepageSearch,
  createHomePageQueryRequestBody,
} from "@fwa/src/services/searchService";
import { fetcher } from "@fwa/src/services/apiClient";
import { numberWithCommas } from "@fwa/src/utils/formatUtils";

import { HomePageSearchResult } from "@fwa/src/components/HomePageSearchResult";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/HomePageSearch/styles.module.css";

import type {
  SearchResultPageType,
  SearchResultTeamType,
  SearchHitsType,
  SearchResultType,
} from "@fwa/src/types";

const ITEMS_PER_PAGE = 10;

export const HomePageSearch = () => {
  const { trackEventGtm, trackError } = useTracking();
  const router = useRouter();
  const { q, page } = router?.query || {};
  const searchQuery: string = q ? q.toString() : "";
  const currentPage: number = page ? parseInt(page.toString(), 10) : 1;
  const [searchString, setSearchString] = useState<string>(searchQuery);
  const [text, setText] = useState<string>(searchQuery);
  const [urlKey, setUrlKey] = useState<string | null>(null);
  const [searchItemOffset, setSearchItemOffset] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const searchFetcher = () =>
    fetcher<SearchHitsType<SearchResultTeamType | SearchResultPageType>>(
      searchUrlHomepageSearch() || "",
      {
        method: "POST",
        body: JSON.stringify(
          createHomePageQueryRequestBody(
            searchItemOffset,
            ITEMS_PER_PAGE,
            searchString,
          ),
        ),
      },
    );

  const { data: searchResponse, error } = useSWR(urlKey, searchFetcher, {});

  const showingText = searchResponse?.hits.total
    ? `Showing ${searchItemOffset + 1} to ${
        searchItemOffset + searchResponse.hits.hits.length
      } of ${numberWithCommas(searchResponse.hits.total)} results`
    : "";

  //  always reset to page 1
  const setSearchHash = () => {
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, q: text, page: 1 },
        },
        undefined,
        { shallow: true },
      )
      .catch((err) => {
        trackError(err as Error, { component: "HomePageSearch" });
      });
  };

  // We are effectively storing page number in the query string
  const setPage = (pageNumber: number) => {
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, page: pageNumber },
        },
        undefined,
        { shallow: true },
      )
      .catch((err) => {
        trackError(err as Error, { component: "HomePageSearch" });
      });
  };

  const handleSearchClick = () => {
    if (text === searchString) return;
    setSearchHash();
  };

  const doSearch = useCallback(
    ({
      searchTerm,
      searchPage,
    }: {
      searchTerm: string;
      searchPage: number;
    }) => {
      if (searchTerm.length < 1) return;
      setIsLoading(true);
      setUrlKey(`${searchUrlHomepageSearch()}-${searchTerm}-${searchPage}`);
    },
    [],
  );

  const handleTextChange = (event: ChangeEvent) => {
    const target = event.target as HTMLInputElement;
    setText(target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Find") {
      e.preventDefault();
      handleSearchClick();
    }
  };

  // if pagination changes, update item offset used in query
  useEffect(() => {
    setSearchItemOffset(ITEMS_PER_PAGE * Math.max(currentPage - 1, 0));

    if (searchResponse || error) {
      resultsContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentPage, searchResponse, error]);

  // if search query string changes update state
  useEffect(() => {
    setText(searchQuery);
    setSearchString(searchQuery);
  }, [searchQuery]);

  // if either of the 2 above changes do new search request
  useEffect(() => {
    if (searchString.length)
      doSearch({ searchTerm: searchString, searchPage: currentPage });
  }, [currentPage, doSearch, searchString]);

  // after we get an error track it
  useEffect(() => {
    if (error && trackError) {
      setIsLoading(false);
      trackError(error as Error, { component: "HomepageSearch" });
    }
  }, [error, trackError]);

  // after we get a response from our query set loading to false and scroll to content
  useEffect(() => {
    if (searchResponse && trackEventGtm) {
      setIsLoading(false);
      trackEventGtm({
        event: "site_search",
        numberOfResults: searchResponse.hits.total || 0,
      });
    }
  }, [searchResponse, trackEventGtm]);

  return (
    <>
      {/* ---------------------- Search section -------------------------------- */}

      <div
        className={[
          "spacing-prop",
          `${sharedStyles["content-wrapper-responsive"]}`,
        ].join(" ")}
        data-margin-top="l"
        data-bg-color="background-light"
        data-margin-bottom="none"
        data-component="home-page-search"
      >
        <div ref={resultsContainerRef} className={styles["search-wrapper"]}>
          <div className={sharedStyles["row-bottom"]}>
            <form action="" className={styles["form"]}>
              <label>
                <Text
                  as="span"
                  textSize="xl"
                  textFontFamily="headings"
                  paddingBottom="xs"
                >
                  Find a Giving Page
                </Text>
                <Text
                  as="span"
                  textFontFamily="headings"
                  textSize="l"
                  paddingBottom="xs"
                  style={{ display: "block" }}
                >
                  Search for friends and teams...
                </Text>
                <TextField
                  label=""
                  type="search"
                  name="search"
                  aria-label="Search friends, teams and events"
                  value={text}
                  autoComplete="off"
                  onChange={handleTextChange}
                  onKeyDown={handleKeyDown}
                  style={{
                    borderRightWidth: 0,
                    marginBottom: 0,
                  }}
                />
              </label>
            </form>
            <Button
              type="button"
              appearance="tertiary"
              aria-label="search"
              onClick={handleSearchClick}
              style={{
                backgroundColor: `var(--clr-background-light, #fff)`,
                borderColor: `var(--clr-cruk-grey-900, #f0f0f0)`,
                borderWidth: `1px 1px 1px 0`,
                borderStyle: "solid",
                borderRadius: 0,
              }}
            >
              <IconFa faIcon={faSearch} size="1.5em" />
            </Button>
          </div>
        </div>
      </div>

      {/* ---------------------- Search results -------------------------------- */}
      {isLoading ? <Loader /> : null}
      {!!error && (
        <Box padding="s" marginBottom="none">
          <ErrorText>Something went wrong please try again later</ErrorText>
        </Box>
      )}

      {!!searchResponse && !searchResponse.hits.hits.length && (
        <div
          className={[
            styles["search-suggestions-wrapper"],
            "spacing-props",
          ].join(" ")}
          data-margin-vertical="s"
        >
          <Text marginHorizontal="none" role="alert" marginBottom="s">
            0 results found
          </Text>
          <div
            className={[
              sharedStyles["content-wrapper-responsive"],
              "spacing-props",
            ].join(" ")}
            style={{ backgroundColor: "var(--clr-cruk-navy-100, #e5e5ff)" }}
            data-margin-horizontal="none"
            data-margin-top="s"
          >
            <Box
              style={{ backgroundColor: "var(--clr-cruk-navy-100, #e5e5ff)" }}
              padding="none"
            >
              <Heading h3 marginLeft="xxs" marginBottom="none">
                Search suggestions:
              </Heading>
              <ol className={styles["search-result-ol"]}>
                <li>Check your spelling</li>
                <li>
                  Try searching for different information. You can search by:
                  <ul className={styles["search-result-ul"]}>
                    <li>Fundraiser name</li>
                    <li>Fundraiser email</li>
                    <li>Team name</li>
                    <li>Team members</li>
                    <li>Event name</li>
                    <li>Page title</li>
                    <li>Description</li>
                    <li>Who the page is in memory of</li>
                  </ul>
                </li>
              </ol>
            </Box>
          </div>
        </div>
      )}
      {!!searchResponse && !!searchResponse.hits.hits.length && (
        <div
          className={[styles["search-results-wrapper"], "spacing-props"].join(
            " ",
          )}
          data-padding-horizontal="none"
          data-padding-top="s"
        >
          <Text role="alert" marginLeft="s">
            {showingText}
          </Text>
          <Box>
            <ul className={sharedStyles["list"]}>
              {searchResponse.hits.hits.map(
                (
                  result: SearchResultType<
                    SearchResultTeamType | SearchResultPageType
                  >,
                ) => (
                  <li key={result._id}>
                    <HomePageSearchResult result={result} />
                  </li>
                ),
              )}
            </ul>
          </Box>
          {searchResponse.hits.total > ITEMS_PER_PAGE && (
            <Pagination
              id="home-page-search-pagination-change"
              current={currentPage}
              perPage={ITEMS_PER_PAGE}
              items={searchResponse.hits.total}
              hideLast
              pagerCallback={(n: number) => {
                trackEventGtm({
                  event: "search_page_change",
                  currentPage: n,
                });
                setPage(n);
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default HomePageSearch;
